import { useCallback, useMemo, useState } from "react";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { processError } from "../../../../common/utils/base_error";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { DashboardRh } from "../../domain/models/dashboard_rh";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { initialFuncionariosListaFiltersState, useFuncionariosListaFilters } from "../hooks/Funcionarios_lista/filters";
import { useAuditoresPagination } from "../../../auditoria/presentation/hooks/Auditoria_auditores/pagination";
import { PrincipalDocumentosLista } from "../../../auditoria/domain/models/documentos_lista";
import { useFuncionariosListaPagination } from "../hooks/Funcionarios_lista/pagination";
import { useDropDownLoader } from "../../../../common/hooks/use_dropdown_loader";
import { CentroCustoDropDown } from "../../../../common/domain/models/centro_custo_dropdown";
import { FuncionarioDropDown } from "../../../../common/domain/models/funcionario_dropdown";
import { ReportsTreinamentosFuncionariosForm } from "../schemas/Principal_rh/reports_treinamentos_funcionarios";
import { useReportViewer } from "../../../../config/hooks/utils/report_viewer";
import { useUserAuthStore } from "../../../../stores/global/userAuth";

import CommonConstants from "../../../../common/constants";

/**
 * LoadPrincipalListaFuncState
 */

type LoadPrincipalListaFuncState = {
  IdArquivoModulo?: number;
  page?: number;
  rowsPerPage?: number;
  tipoPesquisa?: number,
  pesquisa?: string,
};

/**
 * usePrincipalRhViewModel
 */

export const usePrincipalRhViewModel = () => {
  const {
    commons: {
      getAllListagemDocUseCase,
      getGedArquivoDataUseCase,
    },
    rh: {
      getDashboardRhInformationUseCase,
    },
    reports: {
      getReportTreinamentosFuncionariosUseCase,
    },
  } = useDependencyInjection();

  const {
    loadCentroCustoDropDown,
    loadFuncionariosDropDown,
  } = useDropDownLoader();

  const backOrNavigate = useBackOrNavigate();

  const { pushErrorNotification } = usePageLoader();

  const { idUsuario } = useUserAuthStore();

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [dashboardRhInformation, setDashboardRhInformation] = useState<DashboardRh>();

  const [docLista, setDocLista] = useState<PrincipalDocumentosLista[]>([]);

  const [centroCustosDropdown, setCentroCustosDropdown] = useState<CentroCustoDropDown[]>([]);
  const [funcionariosDropdown, setFuncionariosDropdown] = useState<FuncionarioDropDown[]>([]);

  const [reportsTreinamentosFuncionariosModalOpen, setReportsTreinamentosFuncionariosModalOpen] = useState(false);

  const pagination = useAuditoresPagination();

  const [reportViewerState, startReportViewerProcess] = useReportViewer();

  const cardIds = useMemo(() => {
    return {
      funcionariosAtivos: 1,
      funcionariosDemitidos: 2,
      avaliacoesHabilidadesEmAtraso: 3,
      examesEmAtraso: 4,
      treinamentosRealizados: 5,
      treinamentosNaoRealizados: 6,
      treinamentosEficazes: 7,
      treinamentosEficazesRealizados: 8,
      documentosVencidos: 9,
      funcionariosFerias: 10,
      funcionariosAfastados: 11,
      funcionariosAvisoPrevio: 12,
    };
  }, [
  ]);

  /**
   * Funções de carregamento
   */

  const loadDashboardRh = useCallback(async () => {
    try {
      const response = await getDashboardRhInformationUseCase.getDashboardRhInformation();
      setDashboardRhInformation(response);
    } catch (e: unknown) {
      setDashboardRhInformation(undefined);
      throw e;
    }
  }, [
    getDashboardRhInformationUseCase,
  ]);

  const loadDocListaState = useCallback(async (funcState?: LoadPrincipalListaFuncState) => {
    try {
      const filteredPage = funcState?.page ?? pagination.page;
      const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;
      await getAllListagemDocUseCase.getAllListagemDoc(
        String(CommonConstants.moduloDocumentos.Rh),
        filteredPage,
        filteredRowsPerPage
      );
      setDocLista(getAllListagemDocUseCase.items);
      pagination.setRowsPerPage(getAllListagemDocUseCase.pageSize);
      pagination.setTotalItems(getAllListagemDocUseCase.totalItems);
    } catch (e: unknown) {
      throw new Error("Falha ao buscar documentos!");
    }
  }, [
    getAllListagemDocUseCase,
    pagination,
  ]);

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {
      await loadCentroCustoDropDown(dt => setCentroCustosDropdown(dt));
      await loadFuncionariosDropDown(dt => setFuncionariosDropdown(dt), { includeCantBeSelected: true });
      await loadDashboardRh();
      await loadDocListaState();
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao carregar os dados!")[0]);
    }
  });

  /**
   * Eventos
   */

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);
      await loadCentroCustoDropDown(dt => setCentroCustosDropdown(dt));
      await loadFuncionariosDropDown(dt => setFuncionariosDropdown(dt), { includeCantBeSelected: true });
      await loadDashboardRh();
      await loadDocListaState();
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao recarregar os dados!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadCentroCustoDropDown,
    loadFuncionariosDropDown,
    loadDashboardRh,
    loadDocListaState,
    pushErrorNotification,
  ]);

  const handleRelatorio = useCallback(async (form: ReportsTreinamentosFuncionariosForm) => {
    try {
      setLoading(true);

      await startReportViewerProcess({
        fetchCallback: async (requiredMimeType) =>
          await getReportTreinamentosFuncionariosUseCase.getReportTreinamentosFuncionarios(
            requiredMimeType,
            form.centroCustos,
            form.dataRealizado,
            form.funcionarios,
            form.dataAvaliacao,
            form.eficaz,
            idUsuario,
          ),
        closeReportModalCallback: () =>
          setReportsTreinamentosFuncionariosModalOpen(false),
      });

    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar relatório!"));
    } finally {
      setLoading(false);
    }
  }, [
    idUsuario,
    getReportTreinamentosFuncionariosUseCase,
    startReportViewerProcess,
    pushErrorNotification,
  ]);

  const handleCardClick = useCallback((cardId: number) => {
    switch (cardId) {
      case cardIds.funcionariosAtivos:
        useFuncionariosListaFilters.setState({ ...initialFuncionariosListaFiltersState, status: 1 });
        useFuncionariosListaPagination.setState({ page: 1 });
        backOrNavigate("/Funcionarios_lista");
        break;

      case cardIds.funcionariosDemitidos:
        useFuncionariosListaFilters.setState({ ...initialFuncionariosListaFiltersState, status: 2 });
        useFuncionariosListaPagination.setState({ page: 1 });
        backOrNavigate("/Funcionarios_lista");
        break;

      case cardIds.avaliacoesHabilidadesEmAtraso:
        useFuncionariosListaFilters.setState({ ...initialFuncionariosListaFiltersState, status: 1, tipoPesquisa: 3, pesquisa: 'A' });
        useFuncionariosListaPagination.setState({ page: 1 });
        backOrNavigate("/Funcionarios_lista");
        break;

      case cardIds.treinamentosRealizados:
        useFuncionariosListaFilters.setState({ ...initialFuncionariosListaFiltersState, status: 1, treinamentos: 2 });
        useFuncionariosListaPagination.setState({ page: 1 });
        backOrNavigate("/Funcionarios_lista");
        break;

      case cardIds.treinamentosNaoRealizados:
        useFuncionariosListaFilters.setState({ ...initialFuncionariosListaFiltersState, status: 1, treinamentos: 1 });
        useFuncionariosListaPagination.setState({ page: 1 });
        backOrNavigate("/Funcionarios_lista");
        break;

      case cardIds.treinamentosEficazes:
        useFuncionariosListaFilters.setState({ ...initialFuncionariosListaFiltersState, status: 1, tipoPesquisa: 4, pesquisa: 'E' });
        useFuncionariosListaPagination.setState({ page: 1 });
        backOrNavigate("/Funcionarios_lista");
        break;

      case cardIds.treinamentosEficazesRealizados:
        useFuncionariosListaFilters.setState({ ...initialFuncionariosListaFiltersState, status: 1, tipoPesquisa: 4, pesquisa: 'E' });
        useFuncionariosListaPagination.setState({ page: 1 });
        backOrNavigate("/Funcionarios_lista");
        break;

      case cardIds.documentosVencidos:
        useFuncionariosListaFilters.setState({ ...initialFuncionariosListaFiltersState, status: 1, documentos: 2 });
        useFuncionariosListaPagination.setState({ page: 1 });
        backOrNavigate("/Funcionarios_lista");
        break;

      case cardIds.funcionariosFerias:
        useFuncionariosListaFilters.setState({ ...initialFuncionariosListaFiltersState, status: 4 });
        useFuncionariosListaPagination.setState({ page: 1 });
        backOrNavigate("/Funcionarios_lista");
        break;

      case cardIds.funcionariosAfastados:
        useFuncionariosListaFilters.setState({ ...initialFuncionariosListaFiltersState, status: 3 });
        useFuncionariosListaPagination.setState({ page: 1 });
        backOrNavigate("/Funcionarios_lista");
        break;

      case cardIds.funcionariosAvisoPrevio:
        useFuncionariosListaFilters.setState({ ...initialFuncionariosListaFiltersState, status: 5 });
        useFuncionariosListaPagination.setState({ page: 1 });
        backOrNavigate("/Funcionarios_lista");
        break;

    }
  }, [
    cardIds,
    backOrNavigate,
  ]);

  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      pagination.setPage(page + 1);;
      await loadDocListaState({ page: page + 1, });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    pagination,
    loadDocListaState,
    pushErrorNotification,
  ]);

  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      pagination.setPage(1);
      pagination.setRowsPerPage(rowsPerPage);

      await loadDocListaState({ page: 1, rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    pagination,
    loadDocListaState,
    pushErrorNotification,
  ]);

  const handleDownloadFile = useCallback(async (IdArquivoModulo: number, NomeArquivo: string) => {
    if (IdArquivoModulo > 0) {
      try {
        setLoading(true);
        await getGedArquivoDataUseCase
          .getGedArquivoData(IdArquivoModulo, NomeArquivo);
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao buscar documentos!'));
      } finally {
        setLoading(false);
      }
    }
  }, [
    getGedArquivoDataUseCase,
    pushErrorNotification,
  ]);

  const handleReportsTreinamentosFuncionariosOpenState = useCallback((open: boolean) => {
    setReportsTreinamentosFuncionariosModalOpen(open);
  }, [
  ]);

  return {
    loading,
    errorMessage,
    dashboardRhInformation: dashboardRhInformation ?? null,
    docLista,
    centroCustosDropdown: centroCustosDropdown ?? [],
    funcionariosDropdown: funcionariosDropdown ?? [],
    reportsTreinamentosFuncionariosModalOpen,
    pagination,
    reportViewerState,
    cardIds,
    handleReload,
    handleRelatorio,
    handleCardClick,
    handlePageChange,
    handleRowsPerPageChange,
    handleDownloadFile,
    handleReportsTreinamentosFuncionariosOpenState,
  };
}